/** @module @category API */ import { useMemo } from "react";
import useSWRImmutable from "swr/immutable";
import { openmrsFetch, restBaseUrl } from "@openmrs/esm-api";
/*
 * Add other properties as needed. Maintain alphabetical order. Keep in lockstep with the interface above.
 */ const customRepProps = [
    [
        'admissionDecisionConcept',
        'ref'
    ],
    [
        'admissionEncounterType',
        'ref'
    ],
    [
        'admissionForm',
        'ref'
    ],
    [
        'atFacilityVisitType',
        'ref'
    ],
    [
        'bedAssignmentEncounterType',
        'ref'
    ],
    [
        'cancelADTRequestEncounterType',
        'ref'
    ],
    [
        'checkInClerkEncounterRole',
        'ref'
    ],
    [
        'checkInEncounterType',
        'ref'
    ],
    [
        'clinicianEncounterRole',
        'ref'
    ],
    [
        'conceptSourcesForDiagnosisSearch',
        'ref'
    ],
    [
        'consultEncounterType',
        'ref'
    ],
    [
        'consultFreeTextCommentsConcept',
        'ref'
    ],
    [
        'denyAdmissionConcept',
        'ref'
    ],
    [
        'diagnosisMetadata',
        'ref'
    ],
    [
        'diagnosisSets',
        'ref'
    ],
    [
        'dischargeForm',
        'ref'
    ],
    [
        'dispositionDescriptor',
        'ref'
    ],
    [
        'dispositions',
        'ref'
    ],
    [
        'emrApiConceptSource',
        'ref'
    ],
    [
        'exitFromInpatientEncounterType',
        'ref'
    ],
    [
        'extraPatientIdentifierTypes',
        'ref'
    ],
    [
        'fullPrivilegeLevel',
        'ref'
    ],
    [
        'highPrivilegeLevel',
        'ref'
    ],
    [
        'identifierTypesToSearch',
        'ref'
    ],
    [
        'inpatientNoteEncounterType',
        'ref'
    ],
    [
        'lastViewedPatientSizeLimit',
        'ref'
    ],
    [
        'metadataSourceName',
        'ref'
    ],
    [
        'motherChildRelationshipType',
        'ref'
    ],
    [
        'narrowerThanConceptMapType',
        'ref'
    ],
    [
        'nonDiagnosisConceptSets',
        'ref'
    ],
    [
        'orderingProviderEncounterRole',
        'ref'
    ],
    [
        'patientDiedConcept',
        'ref'
    ],
    [
        'personImageDirectory',
        'ref'
    ],
    [
        'primaryIdentifierType',
        'ref'
    ],
    [
        'sameAsConceptMapType',
        'ref'
    ],
    [
        'suppressedDiagnosisConcepts',
        'ref'
    ],
    [
        'supportsAdmissionLocationTag',
        '(uuid,display,name,links)'
    ],
    [
        'supportsLoginLocationTag',
        '(uuid,display,name,links)'
    ],
    [
        'supportsTransferLocationTag',
        '(uuid,display,name,links)'
    ],
    [
        'supportsVisitsLocationTag',
        '(uuid,display,name,links)'
    ],
    [
        'telephoneAttributeType',
        'ref'
    ],
    [
        'testPatientPersonAttributeType',
        'ref'
    ],
    [
        'transferForm',
        'ref'
    ],
    [
        'transferRequestEncounterType',
        'ref'
    ],
    [
        'transferWithinHospitalEncounterType',
        'ref'
    ],
    [
        'unknownCauseOfDeathConcept',
        'ref'
    ],
    [
        'unknownLocation',
        'ref'
    ],
    [
        'unknownPatientPersonAttributeType',
        'ref'
    ],
    [
        'unknownProvider',
        'ref'
    ],
    [
        'visitAssignmentHandlerAdjustEncounterTimeOfDayIfNecessary',
        'ref'
    ],
    [
        'visitExpireHours',
        'ref'
    ],
    [
        'visitNoteEncounterType',
        'ref'
    ]
];
const customRepresentation = `custom:${customRepProps.map(([prop, rep])=>`${prop}:${rep}`).join(',')}`;
/**
 * React hook for fetching and managing OpenMRS EMR configuration
 * @returns {Object} Object containing:
 *   - emrConfiguration: EmrApiConfigurationResponse | undefined - The EMR configuration data
 *   - isLoadingEmrConfiguration: boolean - Loading state indicator
 *   - mutateEmrConfiguration: Function - SWR's mutate function for manual revalidation
 *   - errorFetchingEmrConfiguration: Error | undefined - Error object if request fails
 */ export function useEmrConfiguration() {
    const url = `${restBaseUrl}/emrapi/configuration?v=${customRepresentation}`;
    const swrData = useSWRImmutable(url, openmrsFetch);
    const results = useMemo(()=>({
            emrConfiguration: swrData.data?.data,
            isLoadingEmrConfiguration: swrData.isLoading,
            mutateEmrConfiguration: swrData.mutate,
            errorFetchingEmrConfiguration: swrData.error
        }), [
        swrData
    ]);
    return results;
}
